// gating.ts — Wave 3c: the frontend capability contract. Stop gating UI on role-name strings;
// route every access decision through these two predicates (the UI mirror of the server-authoritative
// Wave 3a/3b checks). See _artifacts/WAVE3_FRONTEND_UI_PLAN_2026-06-26.md + [[account-manager-model]].
//
// The model: ONE "Practice Administrator" capability, THREE holders — practice_admin (own practice),
// platform_account_manager (each ASSIGNED practice), platform_admin (any). The god set (pricing,
// platform-staff mgmt, org-tier, billing, org-level marking) is platform_admin ONLY.
//
// Both read the roles[] UNION (mirroring the backend roleIsU — MEMBERSHIP, never rank).

import { useAuthStore } from '../store';
import type { OversightAccessRecord } from '../components/oversight/integrity';

/** The god set — platform_admin ONLY. Gates pricing, platform-staff mgmt, org-tier, billing, and
 *  the org-level / platform-bundle controls in the user editor. */
export function isGodSet(): boolean {
  return useAuthStore.getState().isPlatformAdmin();
}

/**
 * Can the current user administer the CURRENTLY-SELECTED practice as a full practice-admin?
 *   - platform_admin                          → yes (any practice)
 *   - practice_admin                          → yes (they have exactly one practice = their own)
 *   - platform_account_manager                → yes ONLY for an ASSIGNED practice whose per-assignment
 *                                               role (assignment_role) is `account_manager`
 *   - platform_operator / supervisor / others → no (narrower; not admins)
 *
 * Gates every practice-admin surface + every edit control in those sections.
 *
 * Wave 3d (Decision 2): `/me` now carries `assignment_role` on each assignedPractices entry
 * (platform_user_assignments.role), so this reads the per-practice assignment role — full practice-
 * admin ONLY when it is `account_manager` (an operator/supervisor assignment to the same practice is
 * narrower and does NOT unlock the admin surfaces).
 */
export function canAdministerCurrentPractice(): boolean {
  const s = useAuthStore.getState();
  const u = s.user;
  if (!u) return false;
  const roles = u.roles ?? (u.role ? [u.role] : []);
  if (roles.includes('platform_admin')) return true;
  if (roles.includes('practice_admin')) return true;
  if (roles.includes('platform_account_manager')) {
    // Wave 3d (Decision 2) — full practice-admin ONLY when the per-assignment role for the SELECTED
    // practice is account_manager (operator/supervisor assignments are narrower). assignment_role comes
    // from /me (platform_user_assignments.role). Not in an assigned practice → false. Fail-closed: a
    // stale /me without assignment_role yields false (under-grant) until the payload refreshes.
    const cur = s.currentPractice?.practice_id;
    if (!cur) return false;
    const assignment = s.assignedPractices.find((p) => p.practice_id === cur);
    return assignment?.assignment_role === 'account_manager';
  }
  return false;
}

/**
 * The UI MIRROR of backend canConfigurePractice (middleware/auth.ts) for the CURRENTLY-SELECTED practice - who may
 * run the setup wizard, activation, the binder and demo data there: the platform admin; a platform account manager
 * or operator with an assignment row at this practice (any assignment role - the server reads the row, not its
 * role); a practice admin at the selected practice (at these doors both sides of the server's practice-admin compare
 * are the practice the request resolved - the home or a live org sister - so an org-level admin at a sister is
 * admitted too). The server decides every door; this only decides what RENDERS, so it fails CLOSED and must never be
 * wider than the server (ai.359 F11).
 */
export function canConfigureCurrentPractice(): boolean {
  if (isGodSet()) return true;
  const s = useAuthStore.getState();
  const u = s.user;
  if (!u) return false;
  const cur = s.currentPractice?.practice_id;
  if (!cur) return false;
  const roles = u.roles ?? (u.role ? [u.role] : []);
  if (roles.includes('platform_account_manager') || roles.includes('platform_operator')) {
    return s.assignedPractices.some((p) => p.practice_id === cur);
  }
  if (roles.includes('practice_admin')) return true;
  return false;
}

/**
 * Can the current user SET a PLATFORM-ONLY capability for the CURRENTLY-SELECTED practice?
 *
 * Founder ruling 2026-09-13 (§8 + §10): the two AI Assist capability grants (`ai_assist.use`,
 * `ai_assist.open_ask`) and the `ai_assist` MODULE switch may be turned on OR off only by a
 * platform_admin, or by the account manager ASSIGNED TO THAT PRACTICE. A practice_admin may not —
 * that is the single difference from `canAdministerCurrentPractice` above, which admits them.
 * Symmetric: the same set may grant and revoke.
 *
 * The UI MIRROR ONLY — the server is the gate (backend middleware/auth.ts
 * `isPlatformConfiguratorForPractice`). This decides whether a control renders LOCKED (never
 * hidden), so it fails CLOSED: a stale `/me` with no `assignment_role` yields false and the control
 * stays locked with its reason until the payload refreshes (an under-grant, never an escalation).
 *
 * It deliberately keeps the SAME roles guard shape as canAdministerCurrentPractice — the
 * assignment_role lookup runs only for a holder of `platform_account_manager` — so a future `/me`
 * that populated assignedPractices for a practice user could not unlock a control the server refuses.
 */
export function canSetPlatformOnlyCapabilities(): boolean {
  if (isGodSet()) return true;
  const s = useAuthStore.getState();
  const u = s.user;
  if (!u) return false;
  const roles = u.roles ?? (u.role ? [u.role] : []);
  if (!roles.includes('platform_account_manager')) return false;
  const cur = s.currentPractice?.practice_id;
  if (!cur) return false;
  const assignment = s.assignedPractices.find((p) => p.practice_id === cur);
  return assignment?.assignment_role === 'account_manager';
}

/**
 * The AI prompt library menu door (ai.339 AG-4): the platform admin, or a user who is the ACCOUNT
 * MANAGER of at least one assigned practice — read from /me `assignment_role`
 * (platform_user_assignments.role), never a role-name string.
 *
 * Deliberately NOT keyed on the selected practice (critic C7): an account manager with no practice
 * selected still sees the entry, and the section itself shows the "Pick a practice at the top" card
 * for the server's 400 PRACTICE_REQUIRED. The server decides every door (requirePromptLibraryAuthor,
 * keyed on the SESSION practice); this is the UI mirror only, so it fails CLOSED: practice users
 * (assignedPractices empty, or org siblings without `assignment_role`) and a stale /me without
 * `assignment_role` yield false (an under-grant, never an escalation).
 */
export function canAuthorPrompts(): boolean {
  if (isGodSet()) return true;
  return useAuthStore.getState().assignedPractices.some((p) => p.assignment_role === 'account_manager');
}

// ─── ai.123 — the Integrity report (OPS-EXCEPTIONS) ───────────────────────────────────────────────
// These three read ONLY the server-computed access record (GET /api/oversight/access, computed by
// backend services/oversight/gate.ts from the platform-admin membership, the account-manager
// assignment for this practice, and the caller's own-practice rank) — never a role name. They decide
// only what RENDERS; the server re-checks every route. A missing record (loading, failed, Platform
// mode) reads as "no".

/** The Integrity report is switched on AND the server admits this caller to it at the selected practice. */
export function canSeeIntegrityReport(a?: OversightAccessRecord | null): boolean {
  return !!a && a.enabled === true && a.can_view === true;
}

/**
 * The re-check door of an Integrity task. Deliberately NOT tied to `enabled`: the server keeps an
 * existing Integrity task closable through its own doors while the report is switched off.
 */
export function canRecheckIntegrityFinding(a?: OversightAccessRecord | null): boolean {
  return !!a && a.can_view === true;
}

/**
 * The accept door (founder ruling Q-69: a practice admin at their own practice, the account manager
 * assigned to this practice, or a platform admin — a Manager sees the report but cannot accept).
 * Not tied to `enabled`, for the same reason as the re-check door.
 */
export function canAcceptIntegrityFinding(a?: OversightAccessRecord | null): boolean {
  return !!a && a.can_accept === true;
}
